import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { MapPin, Loader2, AlertTriangle } from 'lucide-react';

let cachedApiKey = null;

// Resolve the Google Maps key the SAME way the address autocomplete does:
// prefer the backend-configured key (runtime), fall back to the build-time env
// var. This matters in production where REACT_APP_GOOGLE_PLACES_API_KEY may not
// be baked into the build but the key IS set in Super Admin → System Settings.
async function resolveApiKey() {
  if (cachedApiKey) return cachedApiKey;
  try {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${API_URL}/api/config/google-places`);
    if (res.ok) {
      const d = await res.json();
      if (d.configured && d.api_key) { cachedApiKey = d.api_key; return cachedApiKey; }
    }
  } catch (e) { /* fall through to env */ }
  cachedApiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || null;
  return cachedApiKey;
}

function waitForMaps() {
  return new Promise((resolve) => {
    if (window.google?.maps?.Map) { resolve(); return; }
    const iv = setInterval(() => {
      if (window.google?.maps?.Map) { clearInterval(iv); resolve(); }
    }, 100);
  });
}

async function loadGoogleMaps() {
  // Already available (e.g. loaded by the address autocomplete) → done.
  if (window.google?.maps?.Map) return;
  // A script tag exists (loading in-flight) → just wait for it, never add a
  // second script (Google warns + can break when included twice).
  const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
  if (existing) { await waitForMaps(); return; }

  const apiKey = await resolveApiKey();
  if (!apiKey) throw new Error('missing_api_key');
  await new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('script_load_failed'));
    document.head.appendChild(script);
  });
  await waitForMaps();
}

// Country-aware default map centre (used when a job has no pin/address yet).
// Keeps a UK tenant centred on the UK rather than Brisbane, etc.
const COUNTRY_DEFAULT_CENTER = {
  AU: { lat: -25.2744, lng: 133.7751, zoom: 4 },   // Australia
  NZ: { lat: -41.2865, lng: 174.7762, zoom: 5 },   // New Zealand
  GB: { lat: 54.5, lng: -3.5, zoom: 5 },           // United Kingdom
  UK: { lat: 54.5, lng: -3.5, zoom: 5 },
  IE: { lat: 53.1424, lng: -7.6921, zoom: 6 },     // Ireland
  US: { lat: 39.8283, lng: -98.5795, zoom: 4 },    // United States
  CA: { lat: 56.1304, lng: -106.3468, zoom: 4 },   // Canada
  ZA: { lat: -30.5595, lng: 22.9375, zoom: 5 },    // South Africa
};

export default function PinpointMapModal({ open, onOpenChange, onConfirm, initialLat, initialLng, initialAddress, country }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [address, setAddress] = useState(initialAddress || '');
  const [addressParts, setAddressParts] = useState({ street_address: '', suburb: '', state: '', postcode: '' });
  // Default map centre: existing pin > tenant-country default > Australia.
  const countryDefault = COUNTRY_DEFAULT_CENTER[(country || 'AU').toUpperCase()] || COUNTRY_DEFAULT_CENTER.AU;
  const [coords, setCoords] = useState({
    lat: initialLat || countryDefault.lat,
    lng: initialLng || countryDefault.lng,
  });

  const parseAddressComponents = (components) => {
    const get = (type) => components.find(c => c.types.includes(type));
    const streetNumber = get('street_number')?.long_name || '';
    const route = get('route')?.long_name || '';
    const suburb = get('locality')?.long_name || get('sublocality')?.long_name || get('postal_town')?.long_name || '';
    const state = get('administrative_area_level_1')?.short_name || '';
    const postcode = get('postal_code')?.long_name || '';
    const street_address = [streetNumber, route].filter(Boolean).join(' ').trim();
    return { street_address, suburb, state, postcode };
  };

  const reverseGeocode = useCallback((lat, lng) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setAddress(results[0].formatted_address);
        setAddressParts(parseAddressComponents(results[0].address_components || []));
      }
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    loadGoogleMaps().then(async () => {
      // The Radix dialog animates in, so the map container ref may not be
      // attached the instant Maps is ready. Wait briefly for it instead of
      // bailing (which previously left the spinner running forever).
      let tries = 0;
      while (!cancelled && !mapRef.current && tries < 60) {
        await new Promise((r) => setTimeout(r, 50));
        tries += 1;
      }
      if (cancelled || !mapRef.current) return;
      setLoading(false);

      // Compute the centre fresh here (not from `coords` state) so the latest
      // tenant country is used even if it loaded after this modal first mounted.
      const center = {
        lat: initialLat || countryDefault.lat,
        lng: initialLng || countryDefault.lng,
      };
      setCoords(center);
      const map = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: initialLat ? 16 : countryDefault.zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
          { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
          { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
        ],
      });
      mapInstance.current = map;
      geocoderRef.current = new window.google.maps.Geocoder();

      const marker = new window.google.maps.Marker({
        position: center,
        map,
        draggable: true,
        animation: window.google.maps.Animation.DROP,
      });
      markerRef.current = marker;

      marker.addListener('dragend', () => {
        const pos = marker.getPosition();
        const lat = pos.lat();
        const lng = pos.lng();
        setCoords({ lat, lng });
        reverseGeocode(lat, lng);
      });

      map.addListener('click', (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        marker.setPosition(e.latLng);
        setCoords({ lat, lng });
        reverseGeocode(lat, lng);
      });

      // If we have an initial address but no coords, geocode the address
      if (initialAddress && !initialLat) {
        geocoderRef.current.geocode({ address: initialAddress }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const loc = results[0].geometry.location;
            map.setCenter(loc);
            // Zoom in to street level so the user isn't stranded at country zoom.
            if (results[0].geometry.viewport) {
              map.fitBounds(results[0].geometry.viewport);
            } else {
              map.setZoom(16);
            }
            marker.setPosition(loc);
            setCoords({ lat: loc.lat(), lng: loc.lng() });
            setAddress(results[0].formatted_address);
            setAddressParts(parseAddressComponents(results[0].address_components || []));
          }
        });
      } else if (initialLat) {
        // Reverse geocode the initial coords to populate address parts
        reverseGeocode(initialLat, initialLng);
      }
    }).catch((err) => {
      console.error('Pinpoint map failed to load:', err);
      if (!cancelled) {
        setLoading(false);
        setError(err?.message === 'missing_api_key'
          ? 'Google Maps is not configured. Add a Google Places API key in System Settings.'
          : 'The map failed to load. Please check your connection and try again.');
      }
    });

    return () => { cancelled = true; };
  }, [open]);

  const handleConfirm = () => {
    onConfirm({ latitude: coords.lat, longitude: coords.lng, address, ...addressParts });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#ffd108]" /> Pinpoint Location
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Click on the map or drag the pin to set the exact location.</p>
          <div className="relative w-full h-[400px] rounded-lg border border-border overflow-hidden bg-muted">
            <div ref={mapRef} className="absolute inset-0" data-testid="pinpoint-map" />
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center bg-muted" data-testid="pinpoint-map-error">
                <AlertTriangle className="h-7 w-7 text-amber-500" />
                <span className="text-sm text-foreground max-w-sm">{error}</span>
              </div>
            )}
          </div>
          {address && (
            <div className="p-2 bg-muted rounded text-sm flex items-start gap-2">
              <MapPin className="h-4 w-4 text-[#ffd108] shrink-0 mt-0.5" />
              <span className="text-foreground">{address}</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-volt" onClick={handleConfirm} data-testid="pinpoint-confirm-btn">
            Confirm Location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
